/**
 * Editöre yapıştırılan HTML'i temizler.
 *
 * ChatGPT / Word / web sayfalarından kopyalanan içerik; class, inline style,
 * data-* öznitelikleri ve gereksiz <span>/<div> sarmalları taşıyor. Bunlar
 * myweb şablonlarında ({!! !!} ile ham basılıyor) tasarımı bozuyor.
 * Burada beyaz liste mantığıyla sadece anlamsal etiketler bırakılır.
 */

// İzin verilen etiketler -> o etikette korunacak öznitelikler
const ALLOWED_TAGS = {
    P: [], BR: [], HR: [],
    H1: [], H2: [], H3: [], H4: [], H5: [], H6: [],
    UL: [], OL: [], LI: [],
    STRONG: [], B: [], EM: [], I: [], U: [], S: [], STRIKE: [], DEL: [], INS: [],
    SUB: [], SUP: [],
    BLOCKQUOTE: [], PRE: [], CODE: [],
    A: ['href', 'target', 'rel'],
    IMG: ['src', 'alt', 'title'],
    TABLE: [], THEAD: [], TBODY: [], TFOOT: [], TR: [], TH: [], TD: [],
};

// İçeriğiyle birlikte tamamen atılacak etiketler
const DROP_TAGS = new Set([
    'SCRIPT', 'STYLE', 'NOSCRIPT', 'IFRAME', 'OBJECT', 'EMBED', 'META',
    'LINK', 'HEAD', 'TITLE', 'FORM', 'INPUT', 'TEXTAREA', 'SELECT',
    'BUTTON', 'SVG', 'CANVAS', 'AUDIO', 'VIDEO',
]);

// İçi boşken anlamsız kalan etiketler (<br>, <img>, <hr> hariç)
const REMOVE_IF_EMPTY = new Set([
    'P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'LI', 'UL', 'OL',
    'BLOCKQUOTE', 'STRONG', 'B', 'EM', 'I', 'U', 'S', 'A',
]);

function isSafeUrl(url) {
    const value = String(url || '').trim();
    // javascript:, vbscript:, data: (img hariç) gibi şemaları engelle
    return !/^\s*(javascript|vbscript|data|file)\s*:/i.test(value);
}

function cleanElement(element) {
    // Önce çocukları işle (canlı koleksiyon olmasın diye kopyala)
    Array.from(element.childNodes).forEach((child) => {
        if (child.nodeType === Node.ELEMENT_NODE) {
            cleanElement(child);
        } else if (child.nodeType === Node.COMMENT_NODE) {
            child.remove();
        }
    });

    const tag = element.tagName;

    if (DROP_TAGS.has(tag)) {
        element.remove();
        return;
    }

    const allowedAttrs = ALLOWED_TAGS[tag];

    // Beyaz listede değilse: etiketi kaldır, içeriğini yerinde bırak (span, div, font...)
    if (!allowedAttrs) {
        const parent = element.parentNode;
        if (!parent) return;
        while (element.firstChild) {
            parent.insertBefore(element.firstChild, element);
        }
        element.remove();
        return;
    }

    // İzin verilmeyen tüm öznitelikleri sil (class, style, data-*, id, on*...)
    Array.from(element.attributes).forEach((attr) => {
        if (!allowedAttrs.includes(attr.name.toLowerCase())) {
            element.removeAttribute(attr.name);
        }
    });

    if (tag === 'A') {
        if (!isSafeUrl(element.getAttribute('href'))) {
            element.removeAttribute('href');
        }
        // Dış bağlantılar için güvenli varsayılanlar
        if (element.getAttribute('target') === '_blank') {
            element.setAttribute('rel', 'noopener noreferrer');
        }
    }

    if (tag === 'IMG' && !isSafeUrl(element.getAttribute('src'))) {
        element.remove();
    }
}

function tidy(root) {
    // Ardışık <br>'leri teke indir
    Array.from(root.querySelectorAll('br')).forEach((br) => {
        let next = br.nextSibling;
        while (next) {
            const isBlankText = next.nodeType === Node.TEXT_NODE && !next.textContent.trim();
            const isBr = next.nodeType === Node.ELEMENT_NODE && next.tagName === 'BR';
            if (!isBlankText && !isBr) break;
            const following = next.nextSibling;
            if (isBr) next.remove();
            next = following;
        }
    });

    // Blok sonundaki gereksiz <br>'leri at
    Array.from(root.querySelectorAll('p, li, h1, h2, h3, h4, h5, h6, blockquote')).forEach((block) => {
        while (block.lastChild) {
            const last = block.lastChild;
            const isBlankText = last.nodeType === Node.TEXT_NODE && !last.textContent.trim();
            const isBr = last.nodeType === Node.ELEMENT_NODE && last.tagName === 'BR';
            if (!isBlankText && !isBr) break;
            last.remove();
        }
    });

    // İçi tamamen boşalmış etiketleri temizle (en içten dışa)
    Array.from(root.querySelectorAll('*')).reverse().forEach((el) => {
        if (!REMOVE_IF_EMPTY.has(el.tagName)) return;
        const hasMedia = el.querySelector('img, br, hr');
        if (!hasMedia && !el.textContent.trim()) {
            el.remove();
        }
    });

    wrapLooseInline(root);
}

// <div> gibi sarmallar çözülünce kök seviyede kalan metin/inline düğümleri
// <p> içine alır; aksi halde temaların tipografisi bu metinlere uygulanmaz.
const BLOCK_TAGS = new Set([
    'P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'UL', 'OL', 'LI',
    'BLOCKQUOTE', 'PRE', 'TABLE', 'HR',
]);

function wrapLooseInline(root) {
    let buffer = [];

    const flush = () => {
        if (!buffer.length) return;
        const hasContent = buffer.some((node) => (
            node.nodeType === Node.ELEMENT_NODE || node.textContent.trim()
        ));
        if (hasContent) {
            const paragraph = root.ownerDocument.createElement('p');
            buffer[0].parentNode.insertBefore(paragraph, buffer[0]);
            buffer.forEach((node) => paragraph.appendChild(node));
        }
        buffer = [];
    };

    Array.from(root.childNodes).forEach((node) => {
        const isBlock = node.nodeType === Node.ELEMENT_NODE && BLOCK_TAGS.has(node.tagName);
        if (isBlock) {
            flush();
        } else {
            buffer.push(node);
        }
    });

    flush();
}

/**
 * Verilen HTML'i temizleyip döndürür.
 * @param {string} dirtyHtml
 * @returns {string}
 */
export function sanitizeEditorHtml(dirtyHtml) {
    if (!dirtyHtml || typeof dirtyHtml !== 'string') {
        return '';
    }

    // DOMParser izole bir doküman kullanır; script'ler çalışmaz, img indirilmez
    const doc = new DOMParser().parseFromString(dirtyHtml, 'text/html');
    const body = doc.body;

    if (!body) return '';

    Array.from(body.childNodes).forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) {
            cleanElement(node);
        } else if (node.nodeType === Node.COMMENT_NODE) {
            node.remove();
        }
    });

    tidy(body);

    return body.innerHTML.trim();
}

/**
 * contentEditable içine yapıştırma olayını temiz HTML ile karşılar.
 * Editör kütüphanesinin prop iletimine bağlı olmasın diye olay sarmalayıcı
 * div üzerinde (kabararak) yakalanabilir.
 * @param {ClipboardEvent} event
 */
export function handleEditorPaste(event) {
    const clipboard = event.clipboardData || window.clipboardData;
    if (!clipboard) return;

    const pastedHtml = clipboard.getData('text/html');
    const pastedText = clipboard.getData('text/plain');

    // Yapıştırılacak içerik yoksa tarayıcıya bırak (ör. resim yapıştırma)
    if (!pastedHtml && !pastedText) return;

    event.preventDefault();

    let clean;
    if (pastedHtml) {
        clean = sanitizeEditorHtml(pastedHtml);
    } else {
        // Düz metin: satır sonlarını paragrafa çevir, HTML olarak kaçır
        clean = pastedText
            .split(/\n{2,}/)
            .map((block) => {
                const escaped = block
                    .replace(/&/g, '&amp;')
                    .replace(/</g, '&lt;')
                    .replace(/>/g, '&gt;')
                    .trim()
                    .replace(/\n/g, '<br>');
                return escaped ? `<p>${escaped}</p>` : '';
            })
            .join('');
    }

    if (!clean) return;

    // insertHTML, contentEditable'da 'input' olayını tetikler -> editörün
    // onChange'i çalışır ve React state'i güncellenir.
    document.execCommand('insertHTML', false, clean);
}
